import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  CreateDataSourceInput,
  CreateDataSourceGroupInput,
  UpdateDataSourceInput,
  CreateDataSourceLinkInput,
  UpdateDataSourceLinkInput,
  ListDirectLoadsInput,
  StartDataSourceLoadInput,
  StartLinkLoadInput,
} from '@erp/rpc';

/** How often a link's load history refreshes while a load is running. */
const RUNNING_LOAD_POLL_MS = 5_000;

const LIST_KEY = orpc.core.dataSources.list.queryOptions({
  input: {},
}).queryKey;

const LIST_ALL_LINKS_KEY = orpc.core.dataSources.listAllLinks.queryOptions({
  input: {},
}).queryKey;

export function useDataSources() {
  return useQuery({
    ...orpc.core.dataSources.list.queryOptions({ input: {} }),
    staleTime: 60 * 1000,
  });
}

export function useAllDataSourceLinks() {
  return useQuery({
    ...orpc.core.dataSources.listAllLinks.queryOptions({ input: {} }),
    staleTime: 60 * 1000,
  });
}

export function useCreateDataSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDataSourceInput) =>
      rpcClient.core.dataSources.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useCreateDataSourceGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDataSourceGroupInput) =>
      rpcClient.core.dataSources.createGroup(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useDeleteDataSourceGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpcClient.core.dataSources.deleteGroup({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useUpdateDataSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDataSourceInput) =>
      rpcClient.core.dataSources.update(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useDeleteDataSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpcClient.core.dataSources.delete({ id }),
    // Deleting a data source cascades to its links, so refresh the link caches
    // (both the per-source list and the all-links list) alongside the sources.
    onSuccess: (_data, id) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: LIST_KEY }),
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listLinks.queryOptions({
            input: { dataSourceId: id },
          }).queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: LIST_ALL_LINKS_KEY }),
      ]),
  });
}

export function useUpdateDataSourceGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; groupId: string }) =>
      rpcClient.core.dataSources.updateGroup(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useCreateDataSourceLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDataSourceLinkInput) =>
      rpcClient.core.dataSources.createLink(input),
    onSuccess: (_data, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listLinks.queryOptions({
            input: { dataSourceId: variables.dataSourceId },
          }).queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: LIST_ALL_LINKS_KEY }),
      ]),
  });
}

export function useUpdateDataSourceLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDataSourceLinkInput) =>
      rpcClient.core.dataSources.updateLink(input),
    onSuccess: (data) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listLinks.queryOptions({
            input: { dataSourceId: data.link.dataSourceId },
          }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.validateLink.queryOptions({
            input: { id: data.link.id },
          }).queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: LIST_ALL_LINKS_KEY }),
      ]),
  });
}

export function useDeleteDataSourceLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; dataSourceId: string }) =>
      rpcClient.core.dataSources.deleteLink({ id: input.id }),
    onSuccess: (_data, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listLinks.queryOptions({
            input: { dataSourceId: variables.dataSourceId },
          }).queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: LIST_ALL_LINKS_KEY }),
      ]),
  });
}

export function useDataSourceLinks(dataSourceId: string | null) {
  return useQuery({
    ...orpc.core.dataSources.listLinks.queryOptions({
      input: { dataSourceId: dataSourceId ?? '' },
    }),
    enabled: dataSourceId !== null,
    staleTime: 60 * 1000,
  });
}

export function useValidateDataSourceLink(linkId: string) {
  return useQuery({
    ...orpc.core.dataSources.validateLink.queryOptions({
      input: { id: linkId },
    }),
    staleTime: 30 * 1000,
  });
}

export function useCreateDataSourceLinkTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      rpcClient.core.dataSources.createLinkTable({ id }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: orpc.core.dataSources.validateLink.queryOptions({
          input: { id },
        }).queryKey,
      });
    },
  });
}

export function useSyncDataSourceLinkIndexes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      rpcClient.core.dataSources.syncLinkIndexes({ id }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: orpc.core.dataSources.validateLink.queryOptions({
          input: { id },
        }).queryKey,
      });
    },
  });
}

export function useSyncDataSourceLinkFdwMirror() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      rpcClient.core.dataSources.syncLinkFdwMirror({ id }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: orpc.core.dataSources.validateLink.queryOptions({
          input: { id },
        }).queryKey,
      });
    },
  });
}

/**
 * The load this client most recently started on each link. Its ledger row
 * only appears once the workflow has planned it (resolved the source and cut
 * it into chunks) or recorded that planning failed, so the history keeps
 * polling until the load shows up. Held outside the query cache, which would
 * drop an unobserved entry after its gc time while planning is still running.
 */
const awaitedLoadByLink = new Map<string, string>();

/**
 * A link's direct-load history (newest first) plus the server's default chunk
 * size and parallelism. Polls every 5 s while any listed load is running, or
 * while a load started from this client has not been listed yet.
 */
export function useDataSourceLinkLoads(linkId: string, enabled = true) {
  return useQuery({
    ...orpc.core.dataSources.listLinkLoads.queryOptions({
      input: { linkId, limit: 20 },
    }),
    enabled,
    refetchInterval: (query) => {
      const loads = query.state.data?.loads;
      if (!loads) return false;
      const awaited = awaitedLoadByLink.get(linkId);
      const planning =
        awaited !== undefined && !loads.some((load) => load.loadId === awaited);
      return planning || loads.some((load) => load.status === 'running')
        ? RUNNING_LOAD_POLL_MS
        : false;
    },
  });
}

function linkLoadsKey(linkId: string) {
  return orpc.core.dataSources.listLinkLoads.queryOptions({
    input: { linkId, limit: 20 },
  }).queryKey;
}

export function useStartDataSourceLinkLoad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartLinkLoadInput) =>
      rpcClient.core.dataSources.startLinkLoad(input),
    onSuccess: (data, variables) => {
      awaitedLoadByLink.set(variables.linkId, data.loadId);
      return queryClient.invalidateQueries({
        queryKey: linkLoadsKey(variables.linkId),
      });
    },
  });
}

/**
 * What "Load all" on a data source would do: its links in load order, the
 * links it would skip and why, and the loads that currently block it.
 */
export function useDataSourceLoadPreview(
  dataSourceId: string,
  enabled: boolean,
) {
  return useQuery({
    ...orpc.core.dataSources.previewDataSourceLoad.queryOptions({
      input: { dataSourceId },
    }),
    enabled,
  });
}

/** Every page of the organization's loads, whatever its filters. */
const DIRECT_LOADS_KEY = orpc.core.dataSources.listDirectLoads.key();

/** The loads page's batch history (every page and filter) and per-source overview. */
const LOAD_SUMMARY_KEYS = [
  orpc.core.dataSources.listDirectLoadBatchHistory.key(),
  orpc.core.dataSources.listDataSourceLoadOverview.key(),
];

/**
 * Starts a batch load of a data source. Every planned load is awaited on its
 * link, so each link's history polls until its load shows up, however far
 * down the batch it is.
 */
export const START_DATA_SOURCE_LOAD_MUTATION_KEY = [
  'startDataSourceLoad',
] as const;

export function useStartDataSourceLoad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: START_DATA_SOURCE_LOAD_MUTATION_KEY,
    mutationFn: (input: StartDataSourceLoadInput) =>
      rpcClient.core.dataSources.startDataSourceLoad(input),
    onSuccess: (data) => {
      for (const link of data.links) {
        awaitedLoadByLink.set(link.linkId, link.loadId);
      }
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: DIRECT_LOADS_KEY }),
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listDataSourceLoadBatches.key(),
        }),
        ...LOAD_SUMMARY_KEYS.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
        ...data.links.map((link) =>
          queryClient.invalidateQueries({
            queryKey: linkLoadsKey(link.linkId),
          }),
        ),
      ]);
    },
  });
}

/**
 * The organization's latest batch and one page of its loads across every
 * link, searched, filtered and sorted by the server. The previous page stays
 * on screen while the next one loads. Polls every 5 s while the batch or any
 * load in the organization is running, on this page or not.
 */
export function useDirectLoads(input: ListDirectLoadsInput) {
  return useQuery({
    ...orpc.core.dataSources.listDirectLoads.queryOptions({ input }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const running = data.batch?.status === 'running' || data.running > 0;
      return running ? RUNNING_LOAD_POLL_MS : false;
    },
  });
}

/**
 * A data source's recent batch loads with their end-to-end time, newest
 * first. Polls every 5 s while one is running.
 */
export function useDataSourceLoadBatches(dataSourceId: string) {
  return useQuery({
    ...orpc.core.dataSources.listDataSourceLoadBatches.queryOptions({
      input: { dataSourceId, limit: 10 },
    }),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => batch.status === 'running')
        ? RUNNING_LOAD_POLL_MS
        : false,
  });
}

/**
 * The organization's batch loads from their records, newest first, one page
 * at a time and optionally of one data source. Polls every 5 s while a batch
 * on the page runs, or while `live` (something else on the page runs).
 */
export function useDirectLoadBatchHistory(
  input: { dataSourceId?: string; limit: number; offset: number },
  { live }: { live: boolean },
) {
  return useQuery({
    ...orpc.core.dataSources.listDirectLoadBatchHistory.queryOptions({
      input,
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      live ||
      query.state.data?.batches.some((batch) => batch.status === 'running')
        ? RUNNING_LOAD_POLL_MS
        : false,
  });
}

/**
 * Per source data source: last loaded, last batch, typical batch time and
 * how its links' latest loads ended. Polls like useDirectLoadBatchHistory.
 */
export function useDataSourceLoadOverview({ live }: { live: boolean }) {
  return useQuery({
    ...orpc.core.dataSources.listDataSourceLoadOverview.queryOptions({
      input: {},
    }),
    refetchInterval: (query) =>
      live || query.state.data?.dataSources.some((source) => source.running)
        ? RUNNING_LOAD_POLL_MS
        : false,
  });
}

function useInvalidateLoads() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      [DIRECT_LOADS_KEY, ...LOAD_SUMMARY_KEYS].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
}

export function useCancelDirectLoad() {
  const invalidate = useInvalidateLoads();
  return useMutation({
    mutationFn: (loadId: string) =>
      rpcClient.core.dataSources.cancelDirectLoad({ loadId }),
    onSuccess: invalidate,
  });
}

export function useCancelDirectLoadBatch() {
  const invalidate = useInvalidateLoads();
  return useMutation({
    mutationFn: () => rpcClient.core.dataSources.cancelDirectLoadBatch({}),
    onSuccess: invalidate,
  });
}

export function useCancelAllDirectLoads() {
  const invalidate = useInvalidateLoads();
  return useMutation({
    mutationFn: () => rpcClient.core.dataSources.cancelAllDirectLoads({}),
    onSuccess: invalidate,
  });
}
